import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '../../../components/Button';
import Input from '../../../components/Input';
import Dropdown from '../../../components/Dropdown';
import Toast from '../../../components/Toast';
import Pagination from '../../../components/TableBox/Pagination';
import LoadingRow from '../../../components/TableBox/Rows/LoadingRow';
import ReportsService from '../../../services/reports.service';
import ProductService from '../../../services/product.service';
import PlanService from '../../../services/plan.service';
import MasterService from '../../../services/master.service';
import useSorting from '../../../hooks/useSorting';
import { sortData } from '../../../utils/sortUtils';
import { SortOrder } from '../../../enums';

type ReportTab = 'tenantPortfolio' | 'subscription';

interface Option {
  id: number;
  label: string;
}

const SUBSCRIPTION_STATES = ['active', 'expiring_soon', 'expired', 'cancelled'] as const;

const Reports: React.FC = () => {
  const { t } = useTranslation();
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: '' });
  const [tab, setTab] = useState<ReportTab>('tenantPortfolio');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [productIds, setProductIds] = useState<number[]>([]);
  const [statusIds, setStatusIds] = useState<number[]>([]);
  const [planIds, setPlanIds] = useState<number[]>([]);
  const [subscriptionStates, setSubscriptionStates] = useState<string[]>([]);

  const [productOptions, setProductOptions] = useState<Option[]>([]);
  const [statusOptions, setStatusOptions] = useState<Option[]>([]);
  const [planOptions, setPlanOptions] = useState<Option[]>([]);

  const [previewRows, setPreviewRows] = useState<Record<string, unknown>[]>([]);
  const sortedRows = useMemo(() => sortData(previewRows, orderBy, order), [previewRows, orderBy, order]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    ProductService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      const rows = response?.data?.items ?? [];
      setProductOptions(rows.map((r: any) => ({ id: r.id, label: r.product_name })));
    });
    MasterService.list({ type: 'tenantStatuses', limit: 100 }).then((response: any) => {
      const rows = response?.data?.items ?? [];
      setStatusOptions(rows.map((r: any) => ({ id: r.id, label: r.status_name })));
    });
  }, []);

  useEffect(() => {
    const productId = productIds.length === 1 ? productIds[0] : undefined;
    PlanService.list({ includeDeleted: false, limit: 100, productId }).then((response: any) => {
      const rows = response?.data?.items ?? [];
      setPlanOptions(rows.map((r: any) => ({ id: r.id, label: r.plan_name })));
    });
  }, [productIds]);

  const filterPayload = useMemo(
    () =>
      tab === 'tenantPortfolio'
        ? { startDate: startDate || undefined, endDate: endDate || undefined, product: productIds, status: statusIds, plan: planIds }
        : {
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            product: productIds,
            subscriptionState: subscriptionStates,
            plan: planIds,
          },
    [tab, startDate, endDate, productIds, statusIds, planIds, subscriptionStates]
  );

  const loadPreview = useCallback(() => {
    setLoading(true);
    const request =
      tab === 'tenantPortfolio'
        ? ReportsService.previewTenantPortfolio(filterPayload)
        : ReportsService.previewSubscriptions(filterPayload as any);
    request
      .then((response: any) => {
        setPreviewRows(response?.data?.items ?? []);
        setTotal(response?.data?.total ?? 0);
      })
      .finally(() => setLoading(false));
  }, [tab, filterPayload]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const handleTabChange = useCallback((_e: unknown, value: ReportTab) => {
    setTab(value);
    setStatusIds([]);
    setSubscriptionStates([]);
  }, []);

  const handleExport = useCallback(async () => {
    if (total === 0) return;
    setExporting(true);
    const toastId = toast.info(<Toast message={t('reports.pageTexts.export.generating')} />, {
      icon: false,
      autoClose: false,
    });
    try {
      const response: any =
        tab === 'tenantPortfolio'
          ? await ReportsService.exportTenantPortfolio(filterPayload)
          : await ReportsService.exportSubscriptions(filterPayload as any);

      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${tab === 'tenantPortfolio' ? 'tenants' : 'subscriptions'}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);

      toast.dismiss(toastId);
      toast.success(<Toast message={t('reports.pageTexts.export.success')} />, { icon: false });
    } finally {
      setExporting(false);
    }
  }, [tab, filterPayload, total, t]);

  const previewColumns = previewRows.length > 0 ? Object.keys(previewRows[0]) : [];

  return (
    <div>
      <h4 className="mb-4">{t('reports.pageTexts.title')}</h4>

      <Tabs value={tab} onChange={handleTabChange} className="mb-4">
        <Tab value="tenantPortfolio" label={t('reports.pageTexts.tabs.tenantPortfolio')} />
        <Tab value="subscription" label={t('reports.pageTexts.tabs.subscription')} />
      </Tabs>

      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <Input
            id="startDate"
            name="startDate"
            type="date"
            label={tab === 'tenantPortfolio' ? t('reports.pageTexts.filters.startDate') : t('reports.pageTexts.filters.subscriptionStartDate')}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <Input
            id="endDate"
            name="endDate"
            type="date"
            label={tab === 'tenantPortfolio' ? t('reports.pageTexts.filters.endDate') : t('reports.pageTexts.filters.subscriptionEndDate')}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <Dropdown
            name="products"
            label={t('reports.pageTexts.filters.product')}
            placeholder={t('reports.pageTexts.filters.productPlaceholder')}
            values={productIds.map(String)}
            options={productOptions.map((p) => ({ id: String(p.id), name: p.label }))}
            multi
            noSearch
            onValuesChange={(vals) => setProductIds(vals.map(Number))}
          />
        </div>
        <div className="col-md-3">
          <Dropdown
            name="plans"
            label={t('reports.pageTexts.filters.plan')}
            placeholder={t('reports.pageTexts.filters.planPlaceholder')}
            values={planIds.map(String)}
            options={planOptions.map((p) => ({ id: String(p.id), name: p.label }))}
            multi
            noSearch
            onValuesChange={(vals) => setPlanIds(vals.map(Number))}
          />
        </div>

        {tab === 'tenantPortfolio' ? (
          <div className="col-md-3">
            <Dropdown
              name="statuses"
              label={t('reports.pageTexts.filters.status')}
              placeholder={t('reports.pageTexts.filters.statusPlaceholder')}
              values={statusIds.map(String)}
              options={statusOptions.map((s) => ({ id: String(s.id), name: s.label }))}
              multi
              noSearch
              onValuesChange={(vals) => setStatusIds(vals.map(Number))}
            />
          </div>
        ) : (
          <div className="col-md-3">
            <Dropdown
              name="subscriptionStates"
              label={t('reports.pageTexts.filters.subscriptionState')}
              placeholder={t('reports.pageTexts.filters.subscriptionStatePlaceholder')}
              values={subscriptionStates}
              options={SUBSCRIPTION_STATES.map((st) => ({
                id: st,
                name: t(`reports.pageTexts.filters.subscriptionStates.${st}`),
              }))}
              multi
              noSearch
              onValuesChange={(vals) => setSubscriptionStates(vals)}
            />
          </div>
        )}
      </div>

      <div className="obs-table-card card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div>
            <h6 className="m-0">{t('reports.pageTexts.preview.title')}</h6>
            <span className="text-muted small">
              {t('reports.pageTexts.preview.showing', { shown: previewRows.length, total })}
            </span>
          </div>
          <Button
            className="btn btn-primary"
            text={t('reports.pageTexts.export.button')}
            loading={exporting}
            disabled={total === 0}
            onClick={handleExport}
          />
        </div>
        <hr className="m-0" style={{ borderColor: 'var(--line2)' }} />
        <div className="card-body p-0" style={{ overflowX: 'auto' }}>
        <table className="table obs-table mb-0">
          <thead>
            <tr>
              {previewColumns.length > 0 ? (
                previewColumns.map((col) => {
                  const isSorted = orderBy === col;
                  return (
                    <th
                      key={col}
                      className={`sorting ${isSorted ? (order === SortOrder.asc ? 'sorting_asc' : 'sorting_desc') : ''}`}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        if (orderBy === col) {
                          setOrder(order === SortOrder.asc ? SortOrder.desc : SortOrder.asc);
                        } else {
                          setOrderBy(col);
                          setOrder(SortOrder.asc);
                        }
                      }}
                    >
                      <span className="d-inline-flex align-items-center gap-1 w-100">
                        <span>{col}</span>
                        <span style={{ opacity: isSorted ? 1 : 0.4, transition: 'opacity 0.15s ease' }}>
                          {isSorted ? (
                            order === SortOrder.asc ? (
                              <i className="ri-arrow-up-s-line" style={{ fontSize: '13px', fontWeight: 600 }} />
                            ) : (
                              <i className="ri-arrow-down-s-line" style={{ fontSize: '13px', fontWeight: 600 }} />
                            )
                          ) : (
                            <i className="ri-arrow-up-down-line" style={{ fontSize: '13px' }} />
                          )}
                        </span>
                      </span>
                    </th>
                  );
                })
              ) : (
                <th>{t('reports.pageTexts.preview.title')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <LoadingRow colSpan={previewColumns.length || 5} />
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={previewColumns.length || 5} className="text-muted text-center py-4">{t('reports.pageTexts.preview.empty')}</td>
              </tr>
            ) : (
              sortedRows.slice((page - 1) * perPage, (page - 1) * perPage + perPage).map((row, idx) => (
                <tr key={idx}>
                  {previewColumns.map((col) => (
                    <td key={col}>{row[col] === null || row[col] === undefined ? '—' : String(row[col])}</td>
                  ))}
                </tr>
              )))}
            </tbody>
            </table>
        </div>
        {previewRows.length > 0 && (
          <Pagination
            page={page}
            total={previewRows.length || total}
            perPage={perPage}
            onPageChange={(p) => setPage(p)}
            onPerPageChange={(s) => {
              setPerPage(s);
              setPage(1);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Reports;
